import type { Palette, Shape, Theme } from './tokens';

/**
 * The default theme.
 *
 * Deliberately not "De Vergarde's theme". Nothing in this repository knows what
 * a particular park's colours are; this is a considered neutral that a park
 * either keeps or replaces wholesale, and the second theme next door exists to
 * prove that replacing it is one file and no code.
 *
 * The palette is built around a deep green-teal accent with a warm sand ground,
 * because the product is outdoor accommodation and a cold grey page reads as
 * software rather than as somewhere to stay. Contrast was chosen, not
 * inherited: `ink` on `surface` and `accent-ink` on `accent` both clear 7:1,
 * and `muted` on `ground` clears 4.5:1, so a caption is still a caption in
 * daylight on a phone.
 */
const shape: Shape = {
  // A stack, not a webfont. A guest on a phone at a service station should see
  // text on the first paint, and a 40 kB display face bought at the cost of a
  // blank hero is a bad trade on a page whose job is to be read once.
  'font-body': 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  'font-display': 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  'font-mono': 'ui-monospace, "SF Mono", Menlo, Consolas, monospace',

  'fs-xs': '11.5px',
  'fs-sm': '13px',
  // 16px, and not one pixel less anywhere an input can be focused: iOS zooms
  // the page to any focused field under 16px and never zooms back, which drags
  // the whole layout sideways mid-form.
  'fs-md': '16px',
  'fs-lg': '18px',
  'fs-xl': '22px',
  'fs-2xl': '28px',
  'fs-3xl': '36px',

  'lh-tight': '1.15',
  'lh-body': '1.55',
  'track-tight': '-0.02em',
  'track-wide': '0.12em',
  'weight-body': '400',
  'weight-mid': '560',
  'weight-bold': '700',

  'sp-1': '4px',
  'sp-2': '8px',
  'sp-3': '12px',
  'sp-4': '16px',
  'sp-5': '22px',
  'sp-6': '30px',
  'sp-7': '44px',
  'sp-8': '64px',

  'r-sm': '6px',
  'r-md': '10px',
  'r-lg': '16px',
  'r-xl': '24px',
  'r-pill': '999px',

  'tap': '44px',
  'measure': '62ch',
  'shell': '1120px',
};

const light: Palette = {
  'ground': '#f7f5ef',
  'ground-2': '#efece2',
  'surface': '#ffffff',
  'surface-2': '#f4f2ea',
  'surface-3': '#e8e5d9',
  'ink': '#1b201c',
  'ink-2': '#454f48',
  'muted': '#6a746c',
  'line': '#e0ddd1',
  'line-2': '#cbc7b7',
  'accent': '#1c5c53',
  'accent-2': '#3f8f6b',
  'accent-ink': '#ffffff',
  'accent-soft': '#e2eeea',
  'accent-line': '#b6d3ca',
  'ok': '#1f7a3d',
  'ok-ink': '#155c2d',
  'ok-soft': '#e2f1e6',
  'warn': '#b8790a',
  'warn-ink': '#7d5100',
  'warn-soft': '#fbf0d8',
  'crit': '#b8342f',
  'crit-ink': '#96241f',
  'crit-soft': '#fbe6e4',
  'shadow': '0 1px 2px rgba(27, 32, 28, .07), 0 6px 18px -12px rgba(27, 32, 28, .28)',
  'shadow-lg': '0 2px 6px rgba(27, 32, 28, .08), 0 24px 56px -28px rgba(27, 32, 28, .42)',
  'hero': 'linear-gradient(155deg, #1c5c53 0%, #24705f 46%, #3f8f6b 100%)',
  'hero-ink': '#ffffff',
  'hero-ink-2': 'rgba(255, 255, 255, .82)',
};

const dark: Palette = {
  'ground': '#12150f',
  'ground-2': '#171b14',
  'surface': '#1a1f19',
  'surface-2': '#212720',
  'surface-3': '#2b332a',
  'ink': '#eef1e9',
  'ink-2': '#bcc5ba',
  'muted': '#8f998d',
  'line': '#2b322a',
  'line-2': '#3a4338',
  'accent': '#69c3ac',
  'accent-2': '#8fd7a5',
  'accent-ink': '#08251f',
  'accent-soft': '#16302b',
  'accent-line': '#2c4f47',
  'ok': '#5fc47a',
  'ok-ink': '#7fd894',
  'ok-soft': '#16301d',
  'warn': '#e0a63a',
  'warn-ink': '#ecbe62',
  'warn-soft': '#332714',
  'crit': '#e4736c',
  'crit-ink': '#f09189',
  'crit-soft': '#341d1c',
  'shadow': '0 1px 2px rgba(0, 0, 0, .5), 0 6px 18px -12px rgba(0, 0, 0, .8)',
  'shadow-lg': '0 2px 6px rgba(0, 0, 0, .55), 0 24px 56px -28px rgba(0, 0, 0, .9)',
  'hero': 'linear-gradient(155deg, #0e2b26 0%, #143b32 48%, #1d5344 100%)',
  'hero-ink': '#eef1e9',
  'hero-ink-2': 'rgba(238, 241, 233, .78)',
};

export const defaultTheme: Theme = {
  id: 'meadow',
  label: 'Meadow',
  shape,
  light,
  dark,
};
